import { ColumnType } from 'antd/es/table';
import { sortStrings } from './sort';
import { AnyObject } from 'antd/es/_util/type';

type RecordWithIds = {
  id?: string;
  _id?: string;
};

/**
 * Determines if the ID of a record ID is the same as that of another record, or an ID provided as a string
 * @param record A record with an id or _id field
 * @param otherRecordOrId The record or ID to compare with (if any)
 * @returns true if the record ID matches
 */
export const isSameRecord = (
  record: RecordWithIds | null | undefined,
  otherRecordOrId: RecordWithIds | string | null | undefined
): boolean => {
  if (!otherRecordOrId || !record) return false;

  // If editingItem is a string (e.g. currentActionItemId), compare directly with both IDs
  if (typeof otherRecordOrId === 'string') {
    return record.id === otherRecordOrId || record._id === otherRecordOrId;
  }

  // Check if editingItem has any valid ID to compare against
  const hasId = Boolean(otherRecordOrId.id || otherRecordOrId._id);
  if (!hasId) return false;

  // Ensure boolean return by using Boolean() and explicit comparisons
  return Boolean(
    (record.id && otherRecordOrId.id && record.id === otherRecordOrId.id) ||
      (record._id && otherRecordOrId._id && record._id === otherRecordOrId._id)
  );
};

type GenerateColumnPropsProps<T> = ColumnType<T> & {
  sortable?: boolean;
  dataType?: string;
};

export const generateColumnProps = <T extends AnyObject>({
  sortable = false,
  dataType = 'string',
  ...props
}: GenerateColumnPropsProps<T>): ColumnType<T> => {
  const calcProps: ColumnType<T> = {};
  const dataIndex = props.dataIndex as string;

  if (sortable) {
    if (['string', 'date'].includes(dataType)) {
      calcProps.sorter = (a: T, b: T) =>
        sortStrings(a[dataIndex] as string, b[dataIndex] as string);
    } else {
      calcProps.sorter = (a: T, b: T) => a[dataIndex] - b[dataIndex];
    }
  }
  if (dataType === 'date') {
    calcProps.minWidth = 100;
    calcProps.render = (text: string) => {
      if (!text) return '';
      return new Date(text).toLocaleString(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    };
  }

  return {
    ...calcProps,
    ...props,
  };
};
